// Catalog.ID Client — Hybrid Digital Signatures (Step 2: Ed25519 + ML-DSA-65)
// ============================================================================
// Every logical signing key is a combined Ed25519 + ML-DSA-65 keypair stored as
// a single concatenated blob. Sign/verify execute both algorithms; verification
// requires BOTH to pass — an attacker must defeat both to forge a signature.
//
// Layout:
//   publicKey   = ed25519_pk(32)   || mldsa65_pk(1952)    = 1984 bytes
//   secretSeed  = ed25519_seed(32) || mldsa65_seed(32)    =   64 bytes
//
// Secret material is stored as the per-algorithm seed in both halves: the
// Ed25519 32-byte seed (RFC 8032) and the ML-DSA-65 32-byte private seed
// (FIPS 204). The 4032-byte ML-DSA expanded secret key is a deterministic
// function of the seed; @noble/curves expands the Ed25519 seed internally on
// each sign call, and we re-derive the ML-DSA expanded key from its seed at
// sign time via ml_dsa65.keygen(seed). Storing the seed is equivalent in
// cryptographic content but ~63× smaller per keybag entry, and matches the
// server-side layout in CatalogId.Server.Services.HybridSigningService.
//
// Signature wire form (canonical JSON, RFC 8785 / JCS):
//   {"algorithm":"Hybrid-Ed25519-MLDsa65","ed25519":"<b64>","mldsa65":"<b64>"}

import { ml_dsa65 } from '@noble/post-quantum/ml-dsa.js';
import { ed25519 } from '@noble/curves/ed25519.js';
import { canonicalize } from '@catalog-canonical-json';
import { fromBase64, toBase64 } from './aes';

export const HYBRID_SIG_ALGORITHM = 'Hybrid-Ed25519-MLDsa65';
export const HYBRID_SIG_KEY_ALGORITHM = 'Ed25519-MLDsa65';

/** Layout offsets for the combined hybrid signing keypair. */
export const ED25519_PK_LEN = 32;
export const ED25519_SEED_LEN = 32;        // RFC 8032 seed (expanded internally on sign)
export const MLDSA65_PK_LEN = 1952;
export const MLDSA65_SEED_LEN = 32;        // FIPS 204 private seed (expanded at sign time)
export const HYBRID_PUBLIC_KEY_LEN = ED25519_PK_LEN + MLDSA65_PK_LEN;       // 1984
export const HYBRID_SECRET_SEED_LEN = ED25519_SEED_LEN + MLDSA65_SEED_LEN;  //   64

/** Raw per-algorithm signature sizes, for the concatenated wire form below. */
export const ED25519_SIGNATURE_LEN = 64;
export const MLDSA65_SIGNATURE_LEN = 3309;
/**
 * Length of the concatenated signature form `ed25519_sig(64) || mldsa65_sig(3309)`
 * used on the wire by every .NET verifier
 * (`Catalog.HybridCrypto.HybridEd25519MlDsa65SignatureVerifier`) — CPR
 * federation records, registry blocks, receipts. Distinct from the
 * {@link SignatureWrapper} JSON form CID uses for its own API bodies.
 */
export const HYBRID_COMBINED_SIGNATURE_LEN = ED25519_SIGNATURE_LEN + MLDSA65_SIGNATURE_LEN; // 3373

export interface SignatureWrapper {
    algorithm: string;
    /** Base64 Ed25519 signature (64 bytes). REQUIRED per Decision 4. */
    ed25519: string;
    /** Base64 ML-DSA-65 signature (3309 bytes). */
    mldsa65: string;
}

export interface SigningKeyPair {
    /** Combined ed25519_pk(32) || mldsa65_pk(1952) = 1984 bytes. */
    publicKey: Uint8Array;
    /** Combined ed25519_seed(32) || mldsa65_seed(32) = 64 bytes (the seed pair). */
    secretSeed: Uint8Array;
}

/**
 * Generate a hybrid Ed25519 + ML-DSA-65 signing keypair. The two halves are
 * generated independently with separate randomness — knowing one private seed
 * reveals nothing about the other. Returns the combined public key and the
 * 64-byte concatenated seed pair; both per-algorithm seeds are 32 random bytes.
 */
export function signingKeygen(): SigningKeyPair {
    const edSeed = ed25519.utils.randomSecretKey();
    const edPk = ed25519.getPublicKey(edSeed);

    const pqSeed = crypto.getRandomValues(new Uint8Array(MLDSA65_SEED_LEN));
    const pq = ml_dsa65.keygen(pqSeed);

    const publicKey = new Uint8Array(HYBRID_PUBLIC_KEY_LEN);
    publicKey.set(edPk, 0);
    publicKey.set(pq.publicKey, ED25519_PK_LEN);

    const secretSeed = new Uint8Array(HYBRID_SECRET_SEED_LEN);
    secretSeed.set(edSeed, 0);
    secretSeed.set(pqSeed, ED25519_SEED_LEN);

    return { publicKey, secretSeed };
}

/**
 * Sign a message with both Ed25519 and ML-DSA-65 and return a SignatureWrapper.
 * The hybrid wrapper requires both signatures to verify; an attacker must defeat
 * both algorithms to forge a valid wrapper.
 *
 * The ML-DSA-65 expanded secret key is re-derived from its 32-byte seed on each
 * call via ml_dsa65.keygen(seed). Noble's sign() requires the expanded form, so
 * the expansion happens here rather than at storage time — storage holds only
 * the seed.
 *
 * @param message       — message bytes to sign (same bytes for both algorithms)
 * @param secretSeed    — 64-byte combined seed pair (ed25519_seed(32) || mldsa65_seed(32))
 */
export function signHybrid(message: Uint8Array, secretSeed: Uint8Array): SignatureWrapper {
    if (secretSeed.length !== HYBRID_SECRET_SEED_LEN) {
        throw new Error(
            `signHybrid: expected ${HYBRID_SECRET_SEED_LEN}-byte combined secret seed, got ${secretSeed.length}`,
        );
    }
    const edSeed = secretSeed.subarray(0, ED25519_SEED_LEN);
    const pqSeed = secretSeed.subarray(ED25519_SEED_LEN);

    const edSig = ed25519.sign(message, edSeed);
    const pqExpanded = ml_dsa65.keygen(pqSeed).secretKey;
    const pqSig = ml_dsa65.sign(message, pqExpanded);

    return {
        algorithm: HYBRID_SIG_ALGORITHM,
        ed25519: toBase64(edSig),
        mldsa65: toBase64(pqSig),
    };
}

/**
 * Re-derive the combined public key from a stored 64-byte seed pair. Both
 * halves are deterministic functions of their seed, so the keybag needs to hold
 * only the seed. Used to confirm that a key we are about to sign with is the
 * one a counterparty named.
 */
export function signingPublicKeyFromSeed(secretSeed: Uint8Array): Uint8Array {
    if (secretSeed.length !== HYBRID_SECRET_SEED_LEN) {
        throw new Error(
            `signingPublicKeyFromSeed: expected ${HYBRID_SECRET_SEED_LEN}-byte combined secret seed, got ${secretSeed.length}`,
        );
    }
    const edPk = ed25519.getPublicKey(secretSeed.subarray(0, ED25519_SEED_LEN));
    const pqPk = ml_dsa65.keygen(secretSeed.subarray(ED25519_SEED_LEN)).publicKey;

    const publicKey = new Uint8Array(HYBRID_PUBLIC_KEY_LEN);
    publicKey.set(edPk, 0);
    publicKey.set(pqPk, ED25519_PK_LEN);
    return publicKey;
}

/**
 * Convert a {@link SignatureWrapper} to the concatenated
 * `ed25519_sig || mldsa65_sig` form the .NET verifiers consume. CID's own APIs
 * take the wrapper JSON; anything crossing into Registry/CPR wire records
 * (which type the signature as a plain byte array) needs this form.
 */
export function wrapperToCombinedBytes(wrapper: SignatureWrapper): Uint8Array {
    if (wrapper.algorithm !== HYBRID_SIG_ALGORITHM) {
        throw new Error(`wrapperToCombinedBytes: unsupported algorithm '${wrapper.algorithm}'`);
    }
    const edSig = fromBase64(wrapper.ed25519);
    const pqSig = fromBase64(wrapper.mldsa65);
    if (edSig.length !== ED25519_SIGNATURE_LEN || pqSig.length !== MLDSA65_SIGNATURE_LEN) {
        throw new Error(
            `wrapperToCombinedBytes: expected ${ED25519_SIGNATURE_LEN}+${MLDSA65_SIGNATURE_LEN} signature halves, ` +
            `got ${edSig.length}+${pqSig.length}`,
        );
    }
    const combined = new Uint8Array(HYBRID_COMBINED_SIGNATURE_LEN);
    combined.set(edSig, 0);
    combined.set(pqSig, ED25519_SIGNATURE_LEN);
    return combined;
}

/**
 * The inverse of {@link wrapperToCombinedBytes}: split a concatenated
 * `ed25519_sig(64) || mldsa65_sig(3309)` signature from a durable record back
 * into the wrapper {@link verifyHybrid} consumes. This is the only direction
 * the wrapper is used in for such records — it never reaches one. Throws when
 * the blob is not exactly one combined signature; callers holding untrusted
 * bytes check the length first and treat a mismatch as a verification failure.
 */
export function combinedToWrapper(combined: Uint8Array): SignatureWrapper {
    if (combined.length !== HYBRID_COMBINED_SIGNATURE_LEN) {
        throw new Error(
            `combinedToWrapper: expected a ${HYBRID_COMBINED_SIGNATURE_LEN}-byte combined signature, got ${combined.length}`,
        );
    }
    return {
        algorithm: HYBRID_SIG_ALGORITHM,
        ed25519: toBase64(combined.subarray(0, ED25519_SIGNATURE_LEN)),
        mldsa65: toBase64(combined.subarray(ED25519_SIGNATURE_LEN)),
    };
}

/**
 * Verify a SignatureWrapper. Returns true only if BOTH the Ed25519 and ML-DSA-65
 * signatures verify against their respective public keys. Both verify calls
 * execute unconditionally (Security Invariant 2) — compute each into a separate
 * variable, THEN combine.
 *
 * @param message    — message bytes that were signed
 * @param wrapper    — SignatureWrapper containing both ed25519 and mldsa65 fields
 * @param publicKey  — combined hybrid public key (1984 bytes)
 */
export function verifyHybrid(message: Uint8Array, wrapper: SignatureWrapper, publicKey: Uint8Array): boolean {
    if (!wrapper || typeof wrapper !== 'object') return false;
    if (wrapper.algorithm !== HYBRID_SIG_ALGORITHM) return false;
    if (typeof wrapper.ed25519 !== 'string' || wrapper.ed25519.length === 0) return false;
    if (typeof wrapper.mldsa65 !== 'string' || wrapper.mldsa65.length === 0) return false;
    if (publicKey.length !== HYBRID_PUBLIC_KEY_LEN) return false;

    const edPk = publicKey.subarray(0, ED25519_PK_LEN);
    const pqPk = publicKey.subarray(ED25519_PK_LEN);

    let edSig: Uint8Array;
    let pqSig: Uint8Array;
    try {
        edSig = fromBase64(wrapper.ed25519);
        pqSig = fromBase64(wrapper.mldsa65);
    } catch {
        return false;
    }

    // Security Invariant 2: compute both results into separate variables FIRST
    // (no short-circuit AND), THEN combine. This prevents timing-leak escapes
    // where verification of one half is skipped on first failure.
    let okEd = false;
    let okPq = false;
    try { okEd = ed25519.verify(edSig, message, edPk); } catch { okEd = false; }
    try { okPq = ml_dsa65.verify(pqSig, message, pqPk); } catch { okPq = false; }
    return okEd && okPq;
}

/** Serialize a wrapper to canonical JCS bytes (deterministic UTF-8 output). */
export function wrapperToBytes(wrapper: SignatureWrapper): Uint8Array {
    return canonicalize(wrapper as unknown as { [k: string]: string });
}

/** Serialize a wrapper to its canonical JSON string (storage form for DB text columns and API bodies). */
export function wrapperToJson(wrapper: SignatureWrapper): string {
    return new TextDecoder('utf-8').decode(wrapperToBytes(wrapper));
}

/**
 * Parse a wrapper from a JSON string. Returns null if not a valid wrapper.
 *
 * Per Decision 4 the `ed25519` field is REQUIRED — a wrapper missing it must
 * fail at parse time, not later inside `verifyHybrid`. This tightens the
 * contract so callers don't see optional-looking `wrapper.ed25519` reads.
 */
export function parseWrapper(json: string): SignatureWrapper | null {
    let parsed: unknown;
    try {
        parsed = JSON.parse(json);
    } catch {
        return null;
    }
    if (!parsed || typeof parsed !== 'object') return null;
    const w = parsed as { algorithm?: unknown; ed25519?: unknown; mldsa65?: unknown };
    if (typeof w.algorithm !== 'string' || w.algorithm.length === 0) return null;
    if (typeof w.ed25519 !== 'string' || w.ed25519.length === 0) return null;
    if (typeof w.mldsa65 !== 'string' || w.mldsa65.length === 0) return null;
    return { algorithm: w.algorithm, ed25519: w.ed25519, mldsa65: w.mldsa65 };
}

/**
 * SHA-512 fingerprint of a hybrid signing public key, computed over the raw
 * combined-blob bytes (`ed25519_pk || mldsa65_pk`). Used for Registry's reverse
 * key-owner lookup. Decision 7: storage order matches fingerprint input order
 * — no reordering needed.
 */
export async function computeHybridSigningKeyFingerprint(publicKey: Uint8Array): Promise<Uint8Array> {
    const digest = await crypto.subtle.digest('SHA-512', publicKey as BufferSource);
    return new Uint8Array(digest);
}
